import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Building2, Users, HeartPulse, Trash2, ArrowUpRight, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { AddHospitalDialog } from "@/components/AddHospitalDialog";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function SuperAdmin() {
  const [hospitals, setHospitals] = useState([]);
  const [stats, setStats] = useState({ total_hospitals: 0, total_doctors: 0, total_patients: 0 });

  const load = async () => {
    const [h, s] = await Promise.all([api.listHospitals(), api.stats()]);
    setHospitals(h); setStats(s);
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (code, name) => {
    if (!window.confirm(`Delete ${name}? This removes all its doctors and patients.`)) return;
    await api.deleteHospital(code);
    toast.success(`${name} removed`);
    load();
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <header className="flex items-start justify-between gap-4 mb-10 animate-fade-in-up">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-foreground text-background flex items-center justify-center">
              <Shield className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium">Surgeon.ai</div>
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Super-Admin</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <AddHospitalDialog onCreated={load} />
            <ThemeToggle />
          </div>
        </header>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          {[
            { icon: Building2, label: "Hospitals", value: stats.total_hospitals, testId: "stat-hospitals" },
            { icon: Users, label: "Doctors", value: stats.total_doctors, testId: "stat-doctors" },
            { icon: HeartPulse, label: "Patients", value: stats.total_patients, testId: "stat-patients" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testId} className="surface-card p-5 shadow-sm flex items-start justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium">{s.label}</div>
                <div className="text-3xl font-semibold mt-2 leading-none">{s.value}</div>
              </div>
              <div className="w-9 h-9 rounded-lg border border-border bg-secondary flex items-center justify-center">
                <s.icon className="w-4 h-4 text-muted-foreground" />
              </div>
            </div>
          ))}
        </div>

        {/* Hospitals */}
        <div className="surface-card overflow-hidden shadow-sm">
          <div className="p-5 border-b border-border flex items-center justify-between">
            <div>
              <div className="font-semibold">Registered hospitals</div>
              <div className="text-xs text-muted-foreground">All white-label clients on the platform</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold bg-secondary/40">
                  <th className="p-4">Hospital</th>
                  <th className="p-4">Code</th>
                  <th className="p-4">Brand colors</th>
                  <th className="p-4">Portals</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {hospitals.map((h) => (
                  <tr key={h.code} data-testid={`hospital-row-${h.code}`} className="hover:bg-secondary/40 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg border border-border flex items-center justify-center overflow-hidden">
                          <div className="w-4 h-4 rounded-full" style={{ background: h.primary_color }} />
                        </div>
                        <div>
                          <div className="font-semibold">{h.name}</div>
                          <div className="text-xs text-muted-foreground">{h.logo_text || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="font-mono text-xs px-2 py-1 rounded bg-secondary border border-border">
                        {h.code}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full border border-border" style={{ background: h.primary_color }} title={h.primary_color} />
                        <span className="w-5 h-5 rounded-full border border-border" style={{ background: h.secondary_color }} title={h.secondary_color} />
                        <span className="font-mono text-[11px] text-muted-foreground ml-1">{h.primary_color} · {h.secondary_color}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-2">
                        <Link to={`/${h.code}/doctor`} data-testid={`open-doctor-${h.code}`}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-border bg-card hover:bg-secondary">
                          Doctor <ExternalLink className="w-3 h-3" />
                        </Link>
                        <Link to={`/${h.code}/admin`} data-testid={`open-admin-${h.code}`}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-border bg-card hover:bg-secondary">
                          Admin <ExternalLink className="w-3 h-3" />
                        </Link>
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <Button variant="ghost" size="sm"
                        data-testid={`delete-hospital-${h.code}`}
                        onClick={() => onDelete(h.code, h.name)}
                        className="text-muted-foreground hover:text-rose-500 hover:bg-rose-500/10">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {hospitals.length === 0 && (
                  <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No hospitals yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 text-xs text-muted-foreground flex items-center gap-1">
          <ArrowUpRight className="w-3 h-3" />
          Open <span className="font-mono text-foreground">/&lt;code&gt;/doctor</span> or <span className="font-mono text-foreground">/&lt;code&gt;/patient/&lt;id&gt;</span> to see live branding.
        </div>
      </div>
    </div>
  );
}
